/**
 * Consolidated order service.
 * Handles fetching, existence checks, and custom field updates for orders.
 * Merged from getOrder.ts and updateOrder.ts to resolve circular dependencies.
 */
import { CART, ORDERS, ORDERS_BY_ID } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';
import { countryService } from '../../../shared/services/countryService';
import type { Order } from '../../../shared/types/models';
import * as sessionService from '../../auth/services/session';
import { storeService } from '../../store/services/storeService';
import { ORDER_EXPAND, ORDER_FIELD_KEYS, CustomTypeKey, ORDER_TEXTS, ORDER_STORAGE_KEYS, ORDER_CT_ACTIONS, ORDER_CT_TYPES, ORDER_QUERY_PARAMS, ORDER_POLLING } from '../constants/order.constants';

/**
 * Handle error: log and optionally pass to a callback.
 */
const handleError = (e: unknown, onError?: (message: string) => void): void => {
    console.error('Order service error:', e);
    const err = e as { body?: { message?: string }; message?: string };
    const message =
        (err?.body && err.body.message) || err?.message || ORDER_TEXTS.ERRORS.UNKNOWN_CART_ERROR;
    if (typeof onError === 'function') {
        onError(message);
    } else {
        sessionStorage.setItem('error', message);
    }
};

/**
 * Fetches an order by its ID with expanded fields.
 */
export const getOrderById = async (orderId: string): Promise<Order | null> => {
    try {
        const res = await api.get(ORDERS_BY_ID(orderId), { expand: ORDER_EXPAND });

        if (!res.ok) throw new Error(ORDER_TEXTS.ERRORS.FETCH_FAILED);
        return (await res.json()) as Order;
    } catch (e) {
        handleError(e);
        return null;
    }
};

/**
 * Updates an order with "Amber Country" and "Legal Entity" custom fields.
 * Polls for orderNumber assignment before applying updates.
 */
export const updateOrderCustomFields = async (
    initialOrder: Record<string, unknown>,
    extraActions: Record<string, unknown>[] = []
): Promise<Record<string, unknown>> => {
    if (!initialOrder || !initialOrder.id) return initialOrder;

    try {
        let order = initialOrder;
        let attempts = 0;
        let delayMs: number = ORDER_POLLING.INITIAL_DELAY_MS; // Start at 500ms

        while (!order.orderNumber && attempts < ORDER_POLLING.MAX_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
            const fetchedOrder: unknown = await getOrderById(initialOrder.id as string);
            if (fetchedOrder) {
                order = fetchedOrder as Record<string, unknown>;
            }
            
            // Exponential backoff
            delayMs = Math.min(delayMs * 2, ORDER_POLLING.MAX_DELAY_MS);
            attempts++;
        }

        if (!order.orderNumber) {
            throw new Error(ORDER_TEXTS.ERRORS.POLLING_TIMEOUT);
        }

        const billingAddress = order.billingAddress as Record<string, string> | undefined;

        let isAmberCountryOrder = false;
        if (billingAddress?.country) {
            try {
                const amberList = await countryService.fetchAmberCountries();
                isAmberCountryOrder = amberList.includes(billingAddress.country);
            } catch (e) {
                console.error('Failed to fetch amber countries:', e);
            }
        }

        let legalEntityCode: string | null = null;
        try {
            const storeId = import.meta.env.VITE_STORE_ID;
            if (storeId) {
                const store = await storeService.fetchStore(storeId);
                const storeCustom = (store as { custom?: { fields?: Record<string, unknown> } })?.custom;
                // Check both casing formats for robustness
                legalEntityCode = (storeCustom?.fields?.LegalEntityCode || storeCustom?.fields?.[ORDER_FIELD_KEYS.LEGAL_ENTITY_CODE]) as string | null || null;
            }
        } catch (storeErr) {
            console.error('Failed to fetch store legal entity code:', storeErr);
        }

        // Get user profile info from Ping Identity session
        const userInfo = sessionService.getUserInfoSync();

        const customFields: Record<string, unknown> = {
            [ORDER_FIELD_KEYS.IS_AMBER_COUNTRY]: isAmberCountryOrder,
            [ORDER_FIELD_KEYS.FIRST_NAME]: userInfo?.given_name || '',
            [ORDER_FIELD_KEYS.LAST_NAME]: userInfo?.family_name || '',
            [ORDER_FIELD_KEYS.USER_PROFILE_ID]: userInfo?.user_profile_id || '',
        };
        if (legalEntityCode) {
            customFields[ORDER_FIELD_KEYS.LEGAL_ENTITY_CODE] = legalEntityCode;
        }

        const actions: Record<string, unknown>[] = [
            {
                action: ORDER_CT_ACTIONS.SET_CUSTOM_TYPE,
                type: {
                    key: CustomTypeKey.ORDER_CUSTOM_DATA,
                    typeId: ORDER_CT_TYPES.TYPE,
                },
                fields: customFields,
            },
            ...extraActions,
        ];

        const validateActions = (acts: Record<string, unknown>[]): boolean => {
            const VALID_ACTIONS: string[] = [ORDER_CT_ACTIONS.SET_CUSTOM_TYPE, ORDER_CT_ACTIONS.ADD_PAYMENT, ORDER_CT_ACTIONS.SET_ORDER_NUMBER];
            return acts.every(a => {
                if (!VALID_ACTIONS.includes(a.action as string)) return false;
                if (a.action === ORDER_CT_ACTIONS.ADD_PAYMENT) {
                    const payment = a.payment as Record<string, unknown>;
                    if (!payment?.typeId || !payment?.id) {
                        console.error('Invalid ADD_PAYMENT action: missing typeId or id');
                        return false;
                    }
                }
                return true;
            });
        };

        if (!validateActions(actions)) {
            throw new Error(ORDER_TEXTS.ERRORS.INVALID_CONSOLIDATED_ACTION);
        }

        const updateRes = await api.put(ORDERS_BY_ID(order.id as string), {
            version: order.version,
            actions,
        });

        if (updateRes.ok) {
            const updatedOrder = (await updateRes.json()) as Record<string, unknown>;
            return updatedOrder;
        }
        return order;
    } catch (err) {
        const errMsg = (err as Error).message;
        if (errMsg === ORDER_TEXTS.ERRORS.INVALID_CONSOLIDATED_ACTION || 
            errMsg === ORDER_TEXTS.ERRORS.POLLING_TIMEOUT) {
            throw err;
        }
        console.error('Failed to update order custom fields:', err);
        return initialOrder;
    }
};

/**
 * Ensures an order exists for the given cart ID.
 * If an order already exists for the cart, it is returned (with custom fields applied if missing).
 * Otherwise, a new order is created from the cart.
 */
export const ensureOrderExists = async (
    cartId: string,
    cartVersion: string | number | null,
    extraActions: Record<string, unknown>[] = [],
    skipUpdate: boolean = false
): Promise<Order | null> => {
    try {
        const queryRes = await api.get(ORDERS, {
            where: `cart(id = "${cartId}")`,
            sort: ORDER_QUERY_PARAMS.SORT_DESC,
            limit: ORDER_QUERY_PARAMS.LIMIT_1,
            expand: ORDER_QUERY_PARAMS.EXPAND_PAYMENTS,
        });

        if (queryRes.ok) {
            const queryData = (await queryRes.json()) as { results?: Order[] };
            const results = queryData?.results || [];
            if (results.length > 0) {
                let existingOrder = results[0];
                sessionStorage.setItem(ORDER_STORAGE_KEYS.ORDER_ID, existingOrder.id);

                if (!skipUpdate && (!existingOrder.custom || existingOrder.custom.fields?.[ORDER_FIELD_KEYS.IS_AMBER_COUNTRY] === undefined)) {
                    existingOrder = (await updateOrderCustomFields(existingOrder as unknown as Record<string, unknown>, extraActions)) as unknown as Order;
                }

                return existingOrder;
            }
        }

        let finalVersion = cartVersion;
        try {
            const cartRes = await api.get(CART, { cartId });
            if (cartRes.ok) {
                const cartData = (await cartRes.json()) as { version: number };
                finalVersion = cartData.version;
            }
        } catch (verErr) {
            console.error('Failed to resolve cart version:', verErr);
        }

        if (finalVersion === null || finalVersion === undefined) {
            return null;
        }

        const createRes = await api.post(ORDERS, {
            cart: { id: cartId },
            version: Number(finalVersion),
        });

        if (!createRes.ok) {
            throw new Error(`${ORDER_TEXTS.ERRORS.CREATE_FAILED}: ${createRes.status}`);
        }

        let createdOrder = (await createRes.json()) as Order;
        if (createdOrder?.id) {
            sessionStorage.setItem(ORDER_STORAGE_KEYS.ORDER_ID, createdOrder.id);
            if (!skipUpdate) {
                createdOrder = (await updateOrderCustomFields(createdOrder as unknown as Record<string, unknown>)) as unknown as Order;
            }
        }
        return createdOrder;
    } catch (e) {
        console.error('Failed to ensure order exists:', e);
        try {
            const retry = await api.get(ORDERS, {
                where: `cart(id = "${cartId}")`,
                sort: ORDER_QUERY_PARAMS.SORT_DESC,
                limit: ORDER_QUERY_PARAMS.LIMIT_1,
                expand: ORDER_QUERY_PARAMS.EXPAND_PAYMENTS,
            });

            if (retry.ok) {
                const retryData = (await retry.json()) as { results?: Order[] };
                const results = retryData?.results || [];
                if (results.length > 0) {
                    const existingOrder = results[0];
                    sessionStorage.setItem(ORDER_STORAGE_KEYS.ORDER_ID, existingOrder.id);
                    return existingOrder;
                }
            }
        } catch (retryErr) {
            console.error('Retry query failed:', retryErr);
        }
        return null;
    }
};
